'use client'

import React, { useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { PlatformNames } from '@/app/components/Platforms'
import StoreBadges from '@/app/components/StoreBadges'
import { track } from '@/utils/analytics'
import type { BlogPostMeta } from '@/app/blog/posts'
import { BlogTLDR } from './BlogAeo'

/**
 * Above-the-fold answer card: renders the article's own BlogTLDR verdict above
 * the hero image, with the install ask as one line underneath it.
 *
 * The ask must stay below the verdict. It offers the one thing the article
 * structurally cannot — a check on the product in the reader's hand, rather
 * than the one we chose to write about — and that trade only reads as useful
 * once they've actually got their answer.
 *
 * No dismiss button by design: this is the article's verdict, not an overlay.
 *
 * Fires `hero_answer_*`, deliberately NOT the older `hero_app_cta_*`, which is
 * shared by several unrelated components and so cannot be attributed per-CTA.
 */
type Topic = NonNullable<BlogPostMeta['topic']>

/**
 * One line each. The rule: name the reader's own object, not our product. The
 * article covers the products we picked; the app covers the one they are
 * actually holding. That gap is the entire pitch, and it only makes sense
 * *after* they've read the verdict — which is why it lives here and not above.
 */
const TOPIC_ASK: Record<Topic, { headline: string; sub: string }> = {
  water: { headline: 'Check the bottle you actually own', sub: 'Scan it to see its material, what drives its contact score, and a lower-plastic option.' },
  food: { headline: 'Check the brand in your cupboard', sub: 'Scan the label to see its packaging, what drives its contact score, and a lower-plastic swap.' },
  cosmetics: { headline: 'Check the product on your shelf', sub: 'Paste its ingredient list to see which names match our reference. A name match is not a product test.' },
  pregnancy: { headline: 'Check the bottle or pouch you use', sub: 'Scan it to see its material and contact conditions, and a lower-plastic option.' },
  home: { headline: 'Check the containers in your kitchen', sub: 'Scan what you heat and store food in to see what drives each score, and one swap worth making.' },
  clothing: { headline: 'Check the label on your clothes', sub: 'Scan the fibre label to see its composition and a lower-plastic alternative. The app does not test fabric.' },
  health: { headline: 'Check the products you use most', sub: 'Scan a few everyday items to see what drives each score and where one swap would help.' },
  general: { headline: 'Check a product we did not cover', sub: 'Scan it to see its materials, the evidence behind its score, and a lower-plastic option.' },
}

export interface HeroAnswerProps {
  topic: Topic
  postSlug?: string
  /** The article's own BlogTLDR element, hoisted up by BlogPostLayout. */
  children: React.ReactNode
}

export default function HeroAnswer({ topic, postSlug, children }: HeroAnswerProps) {
  // Preserve the historical render event; use hero_answer_view for visibility.
  const impressionFiredRef = useRef(false)
  const answerRef = useRef<HTMLElement>(null)
  const viewed = useRef(new Set<string>())
  useEffect(() => {
    if (impressionFiredRef.current) return
    impressionFiredRef.current = true
    track('hero_answer_impression', {
      cta_surface: `blog_hero_answer_${topic}`,
      cta_position: 'hero_answer',
      post_topic: topic,
      ...(postSlug ? { post_slug: postSlug } : {}),
    })
  }, [topic, postSlug])

  useEffect(() => {
    const element = answerRef.current
    if (!element || typeof IntersectionObserver === 'undefined') return
    const key = `${topic}:${postSlug ?? ''}`
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25) || viewed.current.has(key)) return
      viewed.current.add(key)
      track('hero_answer_view', {
        cta_surface: `blog_hero_answer_${topic}`, cta_position: 'hero_answer',
        post_topic: topic, reason: 'visibility_25_percent',
        ...(postSlug ? { post_slug: postSlug } : {}),
      })
      observer.disconnect()
    }, { threshold: 0.25 })
    observer.observe(element)
    return () => observer.disconnect()
  }, [topic, postSlug])

  const handleCtaClick = () => {
    track('hero_answer_click', {
      cta_surface: `blog_hero_answer_${topic}`,
      cta_position: 'hero_answer',
      post_topic: topic,
      ...(postSlug ? { post_slug: postSlug } : {}),
    })
  }

  const ask = TOPIC_ASK[topic] ?? TOPIC_ASK.general
  // The hoisted TLDR already contains a padded card. Render its content here
  // so mobile readers get one answer card rather than two nested padded boxes.
  const answerBody = React.isValidElement<{ children?: React.ReactNode }>(children) && children.type === BlogTLDR
    ? children.props.children : children

  return (
    <section
      ref={answerRef}
      aria-label="The short answer"
      className="not-prose mb-8 mt-2 rounded-2xl border border-[#1E6B47]/25 bg-white shadow-sm overflow-hidden"
    >
      {/* The verdict. This is the article's own BlogTLDR, hoisted so it lands in
          the first viewport rather than below a 400px hero image. */}
      <div className="p-5 sm:p-6" role="note" aria-label="Quick answer">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-forest">Quick answer</p>
        <div className="text-base leading-relaxed text-gray-700">{answerBody}</div>
      </div>

      {/* The ask, directly under the verdict. Most readers never scroll past
          the first screen (PostHog, Oct 2026: 24-56% reach 25% scroll), so this
          strip is the article's main install moment. It names the reader's own
          item, says what the app does in one line, and shows the official
          badges. */}
      <div className="border-t border-[#1E6B47]/15 bg-gradient-to-br from-[#1E6B47]/[0.07] to-[#1E6B47]/[0.02] px-5 sm:px-6 py-4">
        <div className="flex items-start gap-3">
          <Image src="/icon1.png" alt="" width={44} height={44} className="mt-0.5 h-11 w-11 shrink-0 rounded-[11px] shadow-sm" />
          <div className="min-w-0">
            <p className="font-display text-[1.05rem] font-bold leading-snug text-ink">{ask.headline}</p>
            <p className="mt-0.5 text-sm leading-snug text-gray-600">{ask.sub}</p>
          </div>
        </div>
        <div className="mt-3.5 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
          {topic === 'cosmetics' ? (
            <Link
              href="/microplastic-ingredient-checker"
              onClick={() => track('related_article_click', {
                cta_surface: 'ingredient_checker_hero', cta_position: 'hero_answer',
                destination: '/microplastic-ingredient-checker', destination_type: 'web_tool',
                post_topic: topic, page_type: 'blog',
                ...(postSlug ? { post_slug: postSlug } : {}),
              })}
              className="w-fit inline-flex items-center justify-center bg-[#1E6B47] text-white text-sm font-semibold px-4 py-2.5 rounded-control hover:bg-[#155235] min-h-[44px] pressable"
            >
              Check ingredient names
            </Link>
          ) : (
            <div className="shrink-0" onClick={handleCtaClick}>
              <StoreBadges
                source={`blog_hero_answer_${topic}`}
                ctaPosition="hero_answer"
                trackingParams={{
                  page_type: 'blog',
                  post_topic: topic,
                  ...(postSlug ? { post_slug: postSlug } : {}),
                }}
                size="sm"
              />
            </div>
          )}
          <p className="text-xs text-gray-500">
            Free · 3 scans · <PlatformNames />
          </p>
        </div>
      </div>
    </section>
  )
}

HeroAnswer.displayName = 'HeroAnswer'
